import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { createRaycastAccelerationForGeometry } from '../../../raycast-acceleration/ensureRaycastAccelerationForObjectTree';
import type { FeatureFace } from '../FeatureFace';
import {
    FEATURE_FACE_MEASURE_BVH_INDIRECT,
    type IFeatureFaceSpatialAccelerationTree,
} from './featureFaceSpatialQueryTypes';

/**
 * 特征面空间加速 Ensurer。
 *
 * 职责：
 * 1. 为 `FeatureFace` 代理几何懒构建 `indirect MeshBVH`；
 * 2. 复用射线加速模块的生命周期与幂等逻辑，禁止测量层直接操作 `boundsTree`；
 * 3. 将底层 BVH 收口为 {@link IFeatureFaceSpatialAccelerationTree} 最小协议供投影/测距消费。
 */
export class FeatureFaceSpatialAccelerationEnsurer {
    /** 测量/投影链路统一使用的 BVH 构建配置。 */
    private static readonly measureBvhConfig = {
        type: MeshBVH,
        indirect: FEATURE_FACE_MEASURE_BVH_INDIRECT,
    } as const;

    /**
     * 确保特征面几何具备可用于空间查询的 BVH，并返回最小协议实例。
     *
     * @param face 特征面代理。
     * @returns 空间加速树；几何不可构建时返回 `null`。
     */
    public static ensure(face: FeatureFace): IFeatureFaceSpatialAccelerationTree | null {
        const geometry = face.geometry;
        if (!(geometry instanceof THREE.BufferGeometry)) {
            return null;
        }

        const positionAttribute = geometry.getAttribute('position');
        if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count <= 0) {
            return null;
        }

        const indexAttribute = geometry.getIndex();
        const triangleVertexCount = indexAttribute instanceof THREE.BufferAttribute
            ? indexAttribute.count
            : positionAttribute.count;
        if (triangleVertexCount < 3) {
            return null;
        }

        createRaycastAccelerationForGeometry(geometry, FeatureFaceSpatialAccelerationEnsurer.measureBvhConfig);

        const candidateTree = Reflect.get(geometry, 'boundsTree');
        if (!candidateTree || typeof candidateTree !== 'object') {
            return null;
        }

        const spatialTree = candidateTree as Partial<IFeatureFaceSpatialAccelerationTree>;
        if (typeof spatialTree.closestPointToPoint !== 'function') {
            return null;
        }

        return spatialTree as IFeatureFaceSpatialAccelerationTree;
    }
}
